/**
 * Web Push for this browser (unit 26A, item 10; v1's `notifications.js`).
 *
 * **A subscription comes back without asking** (v1.82): a cache clear or a
 * reinstall loses it while the permission stays granted, so on open, with the
 * permission granted and no subscription, the app subscribes again. It never
 * prompts there — only the person's own tap in Settings does.
 */
import { api } from "../api/client";
import { ok } from "../api/query";

export type Permission = "default" | "granted" | "denied" | "unsupported";

export function supported(): boolean {
  return (
    typeof navigator !== "undefined" && "serviceWorker" in navigator && "PushManager" in window
  );
}

export function permission(): Permission {
  if (!supported() || typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

function keyBytes(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function current(): Promise<PushSubscription | null> {
  if (!supported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function isSubscribed(): Promise<boolean> {
  return (await current()) !== null;
}

async function register(): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const { public_key } = await ok(api.GET("/api/push/vapid-public-key"));
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(public_key),
  });
  const keys = subscription.toJSON().keys ?? {};
  await ok(
    api.POST("/api/push/subscriptions", {
      body: {
        endpoint: subscription.endpoint,
        p256dh: keys.p256dh ?? "",
        auth: keys.auth ?? "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }),
  );
}

/** The person's tap: ask for the permission, then subscribe. False if refused. */
export async function subscribe(): Promise<boolean> {
  if (!supported()) return false;
  if ((await Notification.requestPermission()) !== "granted") return false;
  await register();
  return true;
}

export async function unsubscribe(): Promise<void> {
  const subscription = await current();
  if (!subscription) return;
  await ok(
    api.DELETE("/api/push/subscriptions", {
      params: { query: { endpoint: subscription.endpoint } },
    }),
  );
  await subscription.unsubscribe();
}

/** On open (v1.82): subscribe again if the permission is granted and the subscription is gone. */
export async function ensureSubscribed(): Promise<boolean> {
  try {
    if (permission() !== "granted") return false;
    if (await isSubscribed()) return true;
    await register();
    return true;
  } catch {
    return false;
  }
}
